import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl, stripDataUrl, downloadB64 } from "../../lib/files";
import { SignaturePad } from "../../components/SignaturePad";
import { toast } from "sonner";
import { Download, Upload, FileSignature, PenLine } from "lucide-react";

const STATUS = {
  da_caricare: "In attesa dell'accordo dall'amministratore",
  inviato: "Accordo ricevuto — da firmare e ricaricare",
  firmato_da_verificare: "Copia firmata inviata — in verifica",
  da_correggere: "Correzioni richieste — ricarica la copia firmata",
  perfezionato: "Accordo perfezionato",
};

export default function AgentAgreement() {
  const [agr, setAgr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPad, setShowPad] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [agentStatus, setAgentStatus] = useState(null);

  const load = async () => {
    const { data } = await http.get("/crm/me/agent/agreement");
    setAgr(data);
    try {
      const me = await http.get("/crm/me/agent");
      setAgentStatus(me.data.status);
      const p = me.data.profile || {};
      setSignerName([p.first_name, p.last_name].filter(Boolean).join(" "));
    } catch { /* noop */ }
  };
  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const original = agr?.versions?.filter((v) => v.kind === "original").slice(-1)[0];
  const signed = agr?.versions?.filter((v) => v.kind === "signed").slice(-1)[0];

  const dl = (v) => downloadB64(v.data_b64, v.filename, v.content_type || "application/pdf");

  const signDevice = async (payload) => {
    setBusy(true);
    try {
      await http.post("/crm/me/agent/agreement/sign-device", payload);
      setShowPad(false);
      await load();
      toast.success("Firma acquisita. PDF firmato generato e inviato in verifica.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const uploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      await http.post("/crm/me/agent/agreement/sign-upload", {
        filename: file.name, data_b64: stripDataUrl(dataUrl), content_type: file.type || "application/pdf",
      });
      await load();
      toast.success("Copia firmata caricata.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  if (!agr) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  const canSign = ["inviato", "da_correggere"].includes(agr.status);

  return (
    <div data-testid="agent-agreement-page" className="max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Accordo di collaborazione</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Puoi firmare l'accordo direttamente sul dispositivo oppure scaricarlo, firmarlo e ricaricare la copia. L'amministratore verifica e perfeziona l'accordo prima dell'attivazione.
      </p>

      <div className="card-clean p-5 mb-5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Stato</div>
        <div className="text-lg font-semibold mb-4" data-testid="agent-agreement-status" style={{ color: "var(--brand-dark)" }}>
          {["profile_incomplete", "integrazioni_richieste", "candidatura_rifiutata"].includes(agentStatus) && agr.status === "da_caricare"
            ? <span data-testid="agent-agreement-prereq">Prima completa il profilo, verifica l'email e invia la candidatura: l'accordo arriva dopo l'approvazione dell'amministratore.</span>
            : agentStatus === "candidatura_inviata" && agr.status === "da_caricare" ? "Candidatura in valutazione: l'accordo arriverà dopo l'approvazione."
            : (STATUS[agr.status] || agr.status)}
        </div>
        {agr.correction_note && <p className="text-sm text-rose-700 mb-3" data-testid="agent-agreement-correction">Nota correzione: {agr.correction_note}</p>}

        <div className="space-y-3">
          {original ? (
            <button onClick={() => dl(original)} data-testid="agent-agreement-download"
              className="px-4 py-2 rounded-md border text-sm flex items-center gap-2" style={{ borderColor: "var(--border-silver)" }}>
              <Download className="w-4 h-4" /> Scarica originale ({original.filename})
            </button>
          ) : <p className="text-sm text-neutral-500">Nessun accordo ancora caricato dall'amministratore.</p>}

          {canSign && (
            <div className="space-y-3" data-testid="agent-sign-options">
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setShowPad((v) => !v)} data-testid="agent-sign-device-btn"
                  className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-primary)" }}>
                  <PenLine className="w-4 h-4" /> Firma sul dispositivo
                </button>
                <label className="px-4 py-2 rounded-md border text-sm flex items-center gap-2 cursor-pointer w-fit" style={{ borderColor: "var(--border-silver)" }} data-testid="agent-agreement-upload-label">
                  <Upload className="w-4 h-4" /> Scarica e carica firmato
                  <input type="file" className="hidden" onChange={uploadSigned} disabled={busy} data-testid="agent-agreement-upload-input" />
                </label>
              </div>
              {showPad && (
                <div className="border rounded-lg p-4" style={{ borderColor: "var(--border-silver)" }}>
                  <p className="text-xs text-neutral-500 mb-2">Firma con dito, penna o mouse. Puoi cancellare e rifare prima di confermare.</p>
                  <SignaturePad signerDefault={signerName} onConfirm={signDevice} />
                </div>
              )}
            </div>
          )}

          {signed && (
            <button onClick={() => dl(signed)} data-testid="agent-agreement-download-signed"
              className="px-4 py-2 rounded-md border text-sm flex items-center gap-2" style={{ borderColor: "var(--border-silver)" }}>
              <FileSignature className="w-4 h-4" /> Scarica copia firmata ({signed.filename})
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-neutral-400">
        Nota: la firma raccolta/caricata sul dispositivo non è un servizio di firma elettronica avanzata (FEA) o qualificata (FEQ). L'attivazione dell'account richiede la verifica dell'accordo da parte dell'amministratore.
      </p>
    </div>
  );
}
